import React, { useEffect, useState, useRef } from "react";
import "./ChatSupport.scss";
import { mixGetAi, mixPostAi } from "../../services/apiAi";
import { CSKH_API_KEY, CSKH_ENGINE_URL } from "../../env";
import { Modal } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import FormattedMarkdown from "../FormattedMarkdown/FormattedMarkdown";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ChatSupportProps {
  user: any;
}

interface Ticket {
  id: string;
  ticket_no: string;
  tenant_id: string;
  seller_id: string;
  customer_id: string;
  channel: string;
  status: string;
  last_intent: string;
  last_message_at: string;
  created_at: string;

  // UI fields
  title: string;
  time: string;
  type: "open" | "resolved";
  category: "clock" | "chat";
  raw?: any;
  rawStatus?: string;
}

const isVideoUrl = (url: string) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
  const urlLower = url.toLowerCase();
  return (
    videoExtensions.some(
      (ext) => urlLower.endsWith(ext) || urlLower.includes(ext + "?"),
    ) || urlLower.startsWith("data:video/")
  );
};

const formatMessageContent = (content: string) => {
  if (!content) return "";
  return <FormattedMarkdown content={content} />;
};

const ChatSupport: React.FC<ChatSupportProps> = ({ user }) => {
  const stores = JSON.parse(localStorage.getItem("stores") || "[]");
  const store_id = localStorage.getItem("storeId");
  //lấy tên gói hiện tại
  const currentStoreName: any = stores.find(
    (item: any) => item.store_id === Number(store_id),
  )?.store_name;
  const currentStorePlan: any = stores.find(
    (item: any) => item.store_id === Number(store_id),
  )?.type_name;

  const [showChatBox, setShowChatBox] = useState(false);
  const [chatView, setChatView] = useState<"list" | "create" | "detail">(
    "list",
  );
  const [selectedTicket, setSelectedTicket] = useState<any>(null);

  const [newSubject, setNewSubject] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newReplyMessage, setNewReplyMessage] = useState("");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketMessages, setTicketMessages] = useState<
    Record<
      string,
      Array<{
        type?: "system-time" | "system-banner" | "user" | "bot" | "agent";
        name?: string;
        text: string;
        time: string;
        subtext?: string;
        image_urls?: string[];
      }>
    >
  >({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeRequestsRef = useRef(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState("");

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(true);
  const messagesRef = useRef<any[]>([]);

  useEffect(() => {
    messagesRef.current = selectedTicket?.id ? (ticketMessages[selectedTicket.id] || []) : [];
  }, [ticketMessages, selectedTicket]);

  useEffect(() => {
    shouldScrollToBottomRef.current = true;
  }, [selectedTicket?.id]);

  const currentTicketStatus = tickets.find((t) => t.id === selectedTicket?.id);
  const isResolved =
    selectedTicket?.type === "resolved" ||
    currentTicketStatus?.type === "resolved";

  const isWaitingForHuman =
    selectedTicket?.rawStatus === "waiting_for_human" ||
    currentTicketStatus?.rawStatus === "waiting_for_human";

  const formatInChicagoTz = (dateVal: any, formatStr: string) => {
    if (!dateVal) return "";
    try {
      const isUnix = typeof dateVal === "number" || /^\d+$/.test(dateVal);
      let d;
      if (isUnix) {
        const valNum = Number(dateVal);
        d = dayjs.unix(valNum < 10000000000 ? valNum : valNum / 1000);
      } else {
        if (
          typeof dateVal === "string" &&
          !dateVal.endsWith("Z") &&
          !dateVal.includes("+") &&
          !dateVal.includes("GMT")
        ) {
          d = dayjs.utc(dateVal);
        } else {
          d = dayjs(dateVal);
        }
      }
      return d.tz("America/Chicago").format(formatStr);
    } catch (error) {
      console.error("Error formatting date in Chicago TZ:", error);
      return dayjs(dateVal).format(formatStr);
    }
  };

  const getRelativeTimeInChicago = (dateVal: any) => {
    if (!dateVal) return "Just now";
    try {
      const isUnix = typeof dateVal === "number" || /^\d+$/.test(dateVal);
      let d;
      if (isUnix) {
        const valNum = Number(dateVal);
        d = dayjs.unix(valNum < 10000000000 ? valNum : valNum / 1000);
      } else {
        if (
          typeof dateVal === "string" &&
          !dateVal.endsWith("Z") &&
          !dateVal.includes("+") &&
          !dateVal.includes("GMT")
        ) {
          d = dayjs.utc(dateVal);
        } else {
          d = dayjs(dateVal);
        }
      }

      if (!d.isValid()) return "Just now";

      const nowChicago = dayjs().tz("America/Chicago");
      const targetChicago = d.tz("America/Chicago");

      const diffInMinutes = nowChicago.diff(targetChicago, "minute");
      if (diffInMinutes < 1) return "Just now";
      if (diffInMinutes < 60)
        return `${diffInMinutes} min${diffInMinutes > 1 ? "s" : ""} ago`;

      const diffInHours = nowChicago.diff(targetChicago, "hour");
      if (diffInHours < 24)
        return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

      const diffInDays = nowChicago.diff(targetChicago, "day");
      if (diffInDays === 1) return "Yesterday";
      return `${diffInDays} days ago`;
    } catch (error) {
      console.error("Error computing relative time in Chicago TZ:", error);
      return "Just now";
    }
  };



  const fetchTickets = async () => {
    if (!store_id) return;
    setLoading(true);
    try {
      const res = await mixGetAi("/conversations/list", {
        customer_id: store_id,
      });
      const conversations = Array.isArray(res) ? res : res?.data || [];
      const mappedTickets = conversations.map((conv: any) => {
        const relativeLastMessageAt = conv.last_message_at
          ? getRelativeTimeInChicago(conv.last_message_at)
          : "Just now";
        const formattedLastMessageAt = conv.last_message_at
          ? formatInChicagoTz(conv.last_message_at, "YYYY-MM-DD HH:mm")
          : "";
        const formattedCreatedAt = conv.created_at
          ? formatInChicagoTz(conv.created_at, "YYYY-MM-DD HH:mm")
          : "";

        return {
          ...conv,
          rawStatus: conv.status,
          last_message_at: formattedLastMessageAt,
          created_at: formattedCreatedAt,
          title: conv.last_intent
            ? `${conv.last_intent}`
            : `Ticket #${conv.id.substring(0, 8)}`,
          status:
            conv.status === "resolved"
              ? "Resolved"
              : conv.status === "waiting_for_human"
                ? "Waiting for Agent"
                : "Active",
          time: relativeLastMessageAt,
          type: conv.status === "resolved" ? "resolved" : "open",
          category: conv.status === "waiting_for_human" ? "clock" : "chat",
        };
      });
      setTickets(mappedTickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (ticketId: string) => {
    const hasMessages = !!ticketMessages[ticketId];
    if (!hasMessages) setLoading(true);
    try {
      const res = await mixGetAi(`/conversations/${ticketId}/messages`, {});
      const msgs = Array.isArray(res) ? res : res?.data || [];
      const mappedMsgs = msgs.map((m: any) => {
        let type: "user" | "bot" | "agent" = "bot";
        if (m.sender_type === "customer") type = "user";
        else if (m.sender_type === "human_agent") type = "agent";
        else if (m.sender_type === "bot") type = "bot";

        let formattedTime = "";
        if (m.created_at) {
          formattedTime = formatInChicagoTz(m.created_at, "HH:mm");
        }

        return {
          type,
          name:
            m.sender_type === "human_agent"
              ? m.agent_name || "Agent"
              : m.sender_type === "bot"
                ? "Support Bot"
                : undefined,
          text: m.content,
          time: formattedTime,
          image_urls: m.image_urls,
        };
      });

      let scrollNeeded = false;
      if (shouldScrollToBottomRef.current) {
        scrollNeeded = true;
        shouldScrollToBottomRef.current = false;
      } else {
        const currentMessages = messagesRef.current;
        const currentLength = currentMessages.length;
        const newLength = mappedMsgs.length;

        if (newLength > currentLength) {
          const lastNewMsg = mappedMsgs[newLength - 1];
          const lastCurrentMsg = currentMessages[currentLength - 1];

          if (
            !lastCurrentMsg ||
            lastNewMsg.text !== lastCurrentMsg.text ||
            lastNewMsg.time !== lastCurrentMsg.time
          ) {
            const container = chatContainerRef.current;
            const isUserAtBottom = container
              ? container.scrollHeight - container.scrollTop - container.clientHeight <= 150
              : true;

            if (lastNewMsg.type === "agent" || lastNewMsg.type === "bot" || isUserAtBottom) {
              scrollNeeded = true;
            }
          }
        }
      }

      setTicketMessages((prev) => {
        const localMsgs = prev[ticketId] || [];
        const fetchedTexts = new Set(mappedMsgs.map((m: any) => m.text));
        const localPending = localMsgs.filter(
          (lm) => lm.type === "user" && !fetchedTexts.has(lm.text),
        );
        return {
          ...prev,
          [ticketId]: [...mappedMsgs, ...localPending],
        };
      });

      if (scrollNeeded) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      if (!hasMessages) setLoading(false);
    }
  };

  const handleTicketClick = (ticket: any) => {
    setSelectedTicket(ticket);
    setChatView("detail");
    shouldScrollToBottomRef.current = true;
    fetchMessages(ticket.id);
  };

  const handleCreateTicket = async () => {
    if (!newSubject.trim() || !store_id) return;
    setSending(true);
    const startTime = Date.now();
    try {
      const messageText =
        newSubject + (newDesc ? `\n\nDetail: ${newDesc}` : "");
      const res = await mixPostAi("/chat/", {
        message: messageText,
        conversation_id: null,
        customer_id: String(store_id),
        tenant_id: String(store_id),
        seller_id: String(user?.creator_id || "0"),
        channel: "app",
        customer_name: currentStoreName || user?.owner_name || "Owner",
        customer_email: user?.email || "",
        customer_context: {
          plan: currentStorePlan || "",
        },
      });

      const elapsed = Date.now() - startTime;
      if (elapsed < 800) {
        await new Promise((resolve) => setTimeout(resolve, 800 - elapsed));
      }

      if (res && res.conversation_id) {
        const newId = res.conversation_id;
        await fetchTickets();

        const newTicket = {
          id: newId,
          rawStatus: res.status,
          title: res.last_intent
            ? res.last_intent
            : `Ticket #${newId.substring(0, 8)}`,
          status:
            res.status === "resolved"
              ? "Resolved"
              : res.status === "waiting_for_human"
                ? "Waiting for Agent"
                : "Active",
          time: "Just now",
          type: res.status === "resolved" ? "resolved" : "open",
          category: res.status === "waiting_for_human" ? "clock" : "chat",
        };

        setSelectedTicket(newTicket);
        setChatView("detail");
        shouldScrollToBottomRef.current = true;
        await fetchMessages(newId);
      }
      setNewSubject("");
      setNewDesc("");
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (isResolved) return;
    if (!newReplyMessage.trim() || !selectedTicket) return;
    const msgToSend = newReplyMessage;
    setNewReplyMessage("");

    activeRequestsRef.current += 1;
    setSending(true);

    const formattedTime = formatInChicagoTz(new Date(), "HH:mm");

    shouldScrollToBottomRef.current = true;
    setTicketMessages((prev) => ({
      ...prev,
      [selectedTicket.id]: [
        ...(prev[selectedTicket.id] || []),
        {
          type: "user",
          text: msgToSend,
          time: formattedTime,
        },
      ],
    }));
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 50);

    const startTime = Date.now();
    try {
      await mixPostAi("/chat/", {
        message: msgToSend,
        conversation_id: selectedTicket.id,
        customer_id: String(store_id),
        tenant_id: String(store_id),
        seller_id: String(user?.creator_id || "0"),
        channel: "web",
        customer_name: currentStoreName || user?.owner_name || "Owner",
        customer_email: user?.email || "",
        customer_context: {
          plan: currentStorePlan || "",
        },
      });

      const elapsed = Date.now() - startTime;
      if (elapsed < 800) {
        await new Promise((resolve) => setTimeout(resolve, 800 - elapsed));
      }

      shouldScrollToBottomRef.current = true;
      await fetchMessages(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      console.error("Error sending reply:", error);
    } finally {
      activeRequestsRef.current -= 1;
      if (activeRequestsRef.current <= 0) {
        setSending(false);
      }
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedTicket) return;

    // Validate size for all files
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > 5 * 1024 * 1024) {
        alert(`File ${files[i].name} exceeds the 5MB size limit.`);
        return;
      }
    }

    setUploadingFile(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("x-api-key", CSKH_API_KEY);

      // Upload files in parallel
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${CSKH_ENGINE_URL}/api/v1/conversations/${selectedTicket.id}/media?sender_type=customer`,
          {
            method: "POST",
            headers: myHeaders,
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Upload failed for ${file.name} with status: ${response.status}`,
          );
        }
      });

      await Promise.all(uploadPromises);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      shouldScrollToBottomRef.current = true;
      await fetchMessages(selectedTicket.id);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to send some or all files");
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    if (showChatBox) {
      fetchTickets();
    }
  }, [showChatBox]);

  useEffect(() => {
    let intervalId: any = null;
    if (
      chatView === "detail" &&
      selectedTicket &&
      selectedTicket.type !== "resolved"
    ) {
      intervalId = setInterval(() => {
        fetchMessages(selectedTicket.id);
      }, 4000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [chatView, selectedTicket]);

  if (user?.role_id === 1 || user?.role_id === 3) {
    return null;
  }

  return (
    <>
      {showChatBox && (
        <div className="floating-chat-box">
          {chatView === "list" ? (
            <>
              <div className="chat-box-header">
                <div style={{ width: 24 }}></div>
                <h3
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  Your tickets
                  {loading && <div className="spinner-mini"></div>}
                </h3>
                <button
                  className="close-btn"
                  onClick={() => setShowChatBox(false)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" fill="#ffffff" />
                    <path
                      d="M8 8L16 16M16 8L8 16"
                      stroke="#000000"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="chat-box-content">
                {loading && tickets.length === 0 ? (
                  <div className="chat-loader">
                    <div className="spinner"></div>
                    <span>Loading conversations...</span>
                  </div>
                ) : (
                  <>
                    <div className="ticket-section">
                      <div className="section-title">Open</div>
                      {tickets
                        .filter((t) => t.type === "open")
                        .map((ticket) => (
                          <div
                            key={ticket.id}
                            className="ticket-card"
                            onClick={() => handleTicketClick(ticket)}
                          >
                            <div
                              className={`ticket-icon-wrapper ${ticket.category}`}
                            >
                              {ticket.category === "clock" && (
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#f2c142"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                              )}
                              {ticket.category === "chat" && (
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#f2c142"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                              )}
                            </div>
                            <div className="ticket-info">
                              <span className="ticket-id">
                                #TK-
                                {ticket.ticket_no
                                  ? ticket.ticket_no.length > 10
                                    ? ticket.ticket_no.substring(0, 8)
                                    : ticket.ticket_no
                                  : ""}
                              </span>
                              <span className="ticket-title">
                                {ticket.title}
                              </span>
                              <span className="ticket-status open">
                                <span className="status-dot"></span>
                                {ticket.status}
                                {ticket.time && ` · ${ticket.time}`}
                              </span>
                            </div>
                            <div className="ticket-arrow">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#808191"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="9 18 15 12 9 6"></polyline>
                              </svg>
                            </div>
                          </div>
                        ))}
                    </div>

                    <div className="ticket-section">
                      <div className="section-title">Resolved</div>
                      {tickets
                        .filter((t) => t.type === "resolved")
                        .map((ticket) => (
                          <div
                            key={ticket.id}
                            className="ticket-card"
                            onClick={() => handleTicketClick(ticket)}
                          >
                            <div className="ticket-icon-wrapper check">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#52c41a"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                            </div>
                            <div className="ticket-info">
                              <span className="ticket-id">
                                #
                                {ticket.ticket_no
                                  ? ticket.ticket_no.length > 10
                                    ? ticket.ticket_no.substring(0, 8)
                                    : ticket.ticket_no
                                  : ""}
                              </span>
                              <span className="ticket-title">
                                {ticket.title}
                              </span>
                              <span className="ticket-status resolved">
                                <span className="status-dot"></span>
                                {ticket.status}
                                {ticket.time && ` · ${ticket.time}`}
                              </span>
                            </div>
                            <div className="ticket-arrow">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#808191"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="9 18 15 12 9 6"></polyline>
                              </svg>
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>

              <div className="chat-box-footer">
                <button
                  className="create-ticket-btn"
                  onClick={() => setChatView("create")}
                >
                  <span className="plus-icon">+</span>
                  Create new ticket
                </button>
              </div>
            </>
          ) : chatView === "create" ? (
            <>
              <div className="chat-box-header">
                <button
                  className="back-btn"
                  onClick={() => setChatView("list")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                </button>
                <h3>Create new ticket</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowChatBox(false)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" fill="#ffffff" />
                    <path
                      d="M8 8L16 16M16 8L8 16"
                      stroke="#000000"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="chat-box-content form-view">
                <div className="form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    placeholder="e.g. POS disconnected"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    disabled={sending}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Describe your issue..."
                    rows={6}
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    disabled={sending}
                  ></textarea>
                </div>
              </div>

              <div className="chat-box-footer">
                <button
                  className="submit-ticket-btn"
                  onClick={handleCreateTicket}
                  disabled={sending}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {sending && (
                    <div
                      className="spinner-mini"
                      style={{ borderTopColor: "#000000" }}
                    ></div>
                  )}
                  {sending ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="chat-box-header">
                <button
                  className="back-btn"
                  onClick={() => setChatView("list")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                </button>
                <div className="header-title-wrapper">
                  <h3
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      justifyContent: "center",
                    }}
                  >
                    Ticket #TK-
                    {selectedTicket?.id
                      ? selectedTicket.id.length > 10
                        ? selectedTicket.id.substring(0, 8)
                        : selectedTicket.id
                      : ""}
                    {loading && <div className="spinner-mini"></div>}
                  </h3>
                  <span className="header-subtitle">
                    {selectedTicket?.time} &bull;{" "}
                    <span
                      className={`status-badge ${isResolved ? "resolved" : selectedTicket?.type || "open"}`}
                    >
                      {isResolved
                        ? "Resolved"
                        : selectedTicket?.status || "Open"}
                    </span>
                  </span>
                </div>
                <button className="menu-more-btn">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="rgba(255, 255, 255, 0.08)"
                    />
                    <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
                    <circle cx="12" cy="12" r="1.5" fill="#ffffff" />
                    <circle cx="16" cy="12" r="1.5" fill="#ffffff" />
                  </svg>
                </button>
              </div>

              <div className="chat-box-content chat-history-view">
                {loading && !ticketMessages[selectedTicket?.id]?.length ? (
                  <div className="chat-loader">
                    <div className="spinner"></div>
                    <span>Loading messages...</span>
                  </div>
                ) : (
                  <div className="messages-list" ref={chatContainerRef}>
                    {(ticketMessages[selectedTicket?.id] || []).map(
                      (msg, index) => {
                        if (msg.type === "system-time") {
                          return (
                            <div
                              key={index}
                              className="chat-system-time-wrapper"
                            >
                              <span className="time-badge">{msg.text}</span>
                              <span className="created-text">
                                {msg.subtext}
                              </span>
                              <span className="created-time">{msg.time}</span>
                            </div>
                          );
                        }
                        if (msg.type === "system-banner") {
                          return (
                            <div
                              key={index}
                              className="chat-system-banner-wrapper"
                            >
                              <div className="chat-system-banner">
                                <svg
                                  className="banner-icon"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#f2c142"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                                </svg>
                                <span className="banner-text">{msg.text}</span>
                              </div>
                              <span className="banner-time">{msg.time}</span>
                            </div>
                          );
                        }

                        const isUser = msg.type === "user";
                        const isBot = msg.type === "bot";

                        return (
                          <div
                            key={index}
                            className={`chat-message-row ${isUser ? "user-row" : "support-row"}`}
                          >
                            {!isUser && (
                              <div className="message-avatar">
                                {isBot ? (
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#f2c142"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect
                                      x="3"
                                      y="11"
                                      width="18"
                                      height="10"
                                      rx="2"
                                    ></rect>
                                    <circle cx="12" cy="5" r="2"></circle>
                                    <path d="M12 7v4"></path>
                                    <line x1="8" y1="16" x2="8" y2="16"></line>
                                    <line
                                      x1="16"
                                      y1="16"
                                      x2="16"
                                      y2="16"
                                    ></line>
                                  </svg>
                                ) : (
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#f2c142"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                  </svg>
                                )}
                              </div>
                            )}
                            <div className="message-bubble-container">
                              <div className="message-bubble">
                                {!isUser && (
                                  <span className="sender-name">
                                    {msg.name || "Support"}
                                  </span>
                                )}
                                <div className="message-text">{formatMessageContent(msg.text)}</div>
                                {msg.image_urls &&
                                  msg.image_urls.length > 0 && (
                                    <div
                                      className="message-images"
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "8px",
                                        marginTop: "8px",
                                      }}
                                    >
                                      {msg.image_urls.map(
                                        (img: string, idx: number) => {
                                          if (isVideoUrl(img)) {
                                            return (
                                              <video
                                                key={idx}
                                                src={img}
                                                style={{
                                                  maxWidth: "200px",
                                                  maxHeight: "150px",
                                                  borderRadius: "8px",
                                                  objectFit: "cover",
                                                  cursor: "pointer",
                                                }}
                                                onClick={() => {
                                                  setLightboxMedia(img);
                                                  setLightboxVisible(true);
                                                }}
                                              />
                                            );
                                          }
                                          return (
                                            <img
                                              key={idx}
                                              src={img}
                                              alt="attachment"
                                              style={{
                                                maxWidth: "200px",
                                                maxHeight: "150px",
                                                borderRadius: "8px",
                                                objectFit: "cover",
                                                cursor: "pointer",
                                              }}
                                              onClick={() => {
                                                setLightboxMedia(img);
                                                setLightboxVisible(true);
                                              }}
                                            />
                                          );
                                        },
                                      )}
                                    </div>
                                  )}
                                <div className="message-meta">
                                  <span className="message-time">
                                    {msg.time}
                                  </span>
                                  {isUser && (
                                    <span className="delivery-status">
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M2 12l5.25 5 11.5-11M8 12l5.25 5 11.5-11"
                                          stroke="#808191"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                    {sending && (
                      <div className="chat-message-row support-row animate-fade-in">
                        <div className="message-avatar">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#f2c142"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="3"
                              y="11"
                              width="18"
                              height="10"
                              rx="2"
                            ></rect>
                            <circle cx="12" cy="5" r="2"></circle>
                            <path d="M12 7v4"></path>
                            <line x1="8" y1="16" x2="8" y2="16"></line>
                            <line x1="16" y1="16" x2="16" y2="16"></line>
                          </svg>
                        </div>
                        <div className="message-bubble-container">
                          <div className="message-bubble typing-bubble">
                            <div className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {isResolved ? (
                <div className="chat-box-resolved-banner">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#808191"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: 6 }}
                  >
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <span>This ticket is resolved and closed for replies.</span>
                </div>
              ) : (
                <div className="chat-box-footer reply-footer">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                    multiple
                    style={{ display: "none" }}
                  />
                  <div className="chat-input-wrapper">
                    <input
                      type="text"
                      placeholder="Add a message..."
                      value={newReplyMessage}
                      onChange={(e) => setNewReplyMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSendReply();
                      }}
                    />
                    {uploadingFile ? (
                      <div
                        className="spinner-mini"
                        style={{ margin: "0 8px" }}
                      ></div>
                    ) : (
                      isWaitingForHuman && (
                        <button
                          className="attach-file-btn"
                          onClick={handleAttachClick}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#808191"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                          </svg>
                        </button>
                      )
                    )}
                  </div>
                  <button
                    className="send-reply-btn"
                    onClick={handleSendReply}
                    disabled={!newReplyMessage.trim()}
                  >
                    {sending && !newReplyMessage.trim() ? (
                      <div
                        className="spinner-mini"
                        style={{ borderTopColor: "#000000" }}
                      ></div>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="#000000"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
              <div className="chat-box-secure-disclaimer">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f2c142"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <span>Your conversations are secure and private</span>
              </div>
            </>
          )}
        </div>
      )}
      <div
        className="floating-chat-btn"
        onClick={() => setShowChatBox(!showChatBox)}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="chat-icon"
        >
          <path
            d="M12 2C6.48 2 2 6.02 2 11C2 13.66 3.3 16.03 5.4 17.57C5.1 18.52 4.3 20.17 4.1 20.57C4 20.8 4.2 21.05 4.5 21C5.2 20.87 7.4 19.98 8.4 19.38C9.5 19.78 10.7 20 12 20C17.52 20 22 15.98 22 11C22 6.02 17.52 2 12 2Z"
            fill="currentColor"
          />
          <circle cx="8.5" cy="11" r="1.5" fill="#ffffff" />
          <circle cx="12" cy="11" r="1.5" fill="#ffffff" />
          <circle cx="15.5" cy="11" r="1.5" fill="#ffffff" />
        </svg>
      </div>

      {/* Lightbox Modal */}
      <Modal
        visible={lightboxVisible}
        footer={null}
        onCancel={() => setLightboxVisible(false)}
        centered
        width={800}
      >
        {isVideoUrl(lightboxMedia) ? (
          <video
            src={lightboxMedia}
            controls
            autoPlay
            style={{ width: "100%", borderRadius: 8 }}
          />
        ) : (
          <img
            src={lightboxMedia}
            alt="Preview"
            style={{ width: "100%", borderRadius: 8 }}
          />
        )}
      </Modal>
    </>
  );
};

export default ChatSupport;

